/**
 * ContactSlideOver — Task 5.2
 *
 * Right-side slide-over panel opened by the "Contact us" header button
 * and the "Help & contact" sidebar item. Reproduces the mockup
 * `.slideover` / `.overlay` section with position:fixed so it covers
 * the full Electron window.
 *
 * Context:  useContactPanel  → isOpen, close
 * Auth:     useAuth          → user.email / account.email for prefill
 * Backend:  POST /contact    (mirrors pages/Contact.jsx exactly)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useContactPanel } from '@/contexts/ContactPanelContext';
import { useAuth } from '@/hooks/useAuth';
import { collectClientContext } from '@/lib/clientContext';
import { MessageCircle, X, Send, Mail, BookOpen, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// Subjects MUST match ALLOWED_SUBJECTS in api/routes/contact.js
const SUBJECTS = [
  'General inquiry',
  'Bug report',
  'Feature request',
  'Billing question',
  'Partnership',
  'Press',
];

export default function ContactSlideOver() {
  const { isOpen, close } = useContactPanel();
  const { user, account } = useAuth();

  const prefillEmail = user?.email || account?.email || '';
  const prefillName = user?.displayName || user?.email || account?.email || 'Scrivar user';

  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [ticketId, setTicketId] = useState(null);

  const honeypotRef = useRef(null);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && isOpen) close();
    },
    [isOpen, close],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset form state when panel closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay so the slide-out animation completes before resetting
      const t = setTimeout(() => {
        setSubject(SUBJECTS[0]);
        setMessage('');
        setStatus('idle');
        setErrorMsg('');
        setTicketId(null);
      }, 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'sending') return;

    setStatus('sending');
    setErrorMsg('');

    try {
      const client = await collectClientContext();
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: prefillName,
          email: prefillEmail,
          subject,
          message: message.trim(),
          appVersion: client.appVersion || undefined,
          platform: client.platform || undefined,
          _hp: honeypotRef.current?.value || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success !== true) {
        throw new Error(data.message || 'Sorry, something went wrong. Please try again.');
      }
      setTicketId(data.ticketId || null);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  }

  // ── overlay (backdrop) ──────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'var(--ds-scrim, rgba(18,20,44,.34))',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
    transition: 'opacity .18s ease',
    zIndex: 40,
  };

  // ── slide-over panel ────────────────────────────────────────────────
  const panelStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 412,
    background: 'var(--ds-content, #fff)',
    borderLeft: '1px solid var(--ds-line-2)',
    boxShadow: '0 12px 36px -8px rgba(var(--ds-shadow-rgb, 20,22,48), .28)',
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform .22s cubic-bezier(.4,0,.2,1)',
    zIndex: 50,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    color: 'var(--ds-ink)',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={overlayStyle}
        onClick={close}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Contact Scrivar support"
        style={panelStyle}
      >
        {/* ── Header ── */}
        <div
          style={{
            padding: '20px 22px 16px',
            borderBottom: '1px solid var(--ds-line)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 13,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              background: 'var(--ds-indigo-50)',
              color: 'var(--ds-indigo)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageCircle size={20} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-.2px',
                marginBottom: 3,
                color: 'var(--ds-ink)',
              }}
            >
              Talk to the Scrivar team
            </h3>
            <p
              style={{
                fontSize: 12.5,
                color: 'var(--ds-ink-3)',
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              Tell us what&apos;s going on — a real person reads every message
              and usually replies within one business day.
            </p>
          </div>

          <button
            type="button"
            onClick={close}
            aria-label="Close contact panel"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: '1px solid var(--ds-line-2)',
              background: 'var(--ds-content, #fff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ds-ink-2)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Body ── */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '20px 22px',
          }}
        >
          {status === 'success' ? (
            <SuccessState ticketId={ticketId} onClose={close} />
          ) : (
            <form id="contact-slideover-form" onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <div
                aria-hidden="true"
                style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
              >
                <label>
                  Leave this field blank
                  <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off" />
                </label>
              </div>

              {/* From (read-only) */}
              <div style={fieldStyle}>
                <label style={labelStyle}>From</label>
                <div
                  style={{
                    ...ctlStyle,
                    color: prefillEmail ? 'var(--ds-ink-3)' : 'var(--ds-ink-4)',
                  }}
                >
                  {prefillEmail || 'Not signed in'}
                </div>
              </div>

              {/* Subject */}
              <div style={fieldStyle}>
                <label htmlFor="cso-subject" style={labelStyle}>
                  What&apos;s this about?
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    id="cso-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    style={{
                      ...ctlStyle,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      cursor: 'pointer',
                      paddingRight: 36,
                    }}
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <span
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: 'var(--ds-ink-3)',
                    }}
                  >
                    ▾
                  </span>
                </div>
              </div>

              {/* Message */}
              <div style={fieldStyle}>
                <label htmlFor="cso-message" style={labelStyle}>
                  Message
                </label>
                <textarea
                  id="cso-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe what you're trying to do or what went wrong…"
                  rows={5}
                  style={{
                    width: '100%',
                    minHeight: 104,
                    border: '1px solid var(--ds-line-2)',
                    borderRadius: 9,
                    background: 'var(--ds-content, #fff)',
                    padding: '11px 12px',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    color: 'var(--ds-ink)',
                    resize: 'vertical',
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Error */}
              {status === 'error' && errorMsg && (
                <div
                  role="alert"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    background: 'var(--ds-red-bg, #FEF2F2)',
                    border: '1px solid var(--ds-red-line, #FECACA)',
                    borderRadius: 9,
                    padding: '10px 12px',
                    fontSize: 12.5,
                    color: 'var(--ds-red-strong, #991B1B)',
                    marginTop: 4,
                  }}
                >
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{errorMsg}</span>
                </div>
              )}
            </form>
          )}
        </div>

        {/* ── Footer ── */}
        {status !== 'success' && (
          <div
            style={{
              borderTop: '1px solid var(--ds-line)',
              padding: '16px 22px',
              background: 'var(--ds-surface-subtle, #FCFCFD)',
              flexShrink: 0,
            }}
          >
            <button
              type="submit"
              form="contact-slideover-form"
              disabled={status === 'sending' || !message.trim()}
              style={{
                width: '100%',
                height: 42,
                borderRadius: 10,
                background: 'linear-gradient(140deg, var(--ds-indigo), var(--ds-indigo-strong))',
                color: 'var(--ds-content, #fff)',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: status === 'sending' || !message.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(var(--ds-indigo-rgb, 79,70,229), .26)',
                opacity: !message.trim() ? 0.6 : 1,
              }}
            >
              {status === 'sending' ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send message
                </>
              )}
            </button>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                marginTop: 13,
              }}
            >
              <a
                href="mailto:support@scrivar.com"
                style={{
                  fontSize: 12,
                  color: 'var(--ds-ink-2)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 500,
                }}
              >
                <Mail size={14} style={{ color: 'var(--ds-ink-3)' }} />
                support@scrivar.com
              </a>
              <a
                href="/help"
                style={{
                  fontSize: 12,
                  color: 'var(--ds-ink-2)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 500,
                }}
              >
                <BookOpen size={14} style={{ color: 'var(--ds-ink-3)' }} />
                Help center
              </a>
            </div>

            <p
              style={{
                textAlign: 'center',
                fontSize: 11.5,
                color: 'var(--ds-ink-4)',
                marginTop: 12,
                marginBottom: 0,
              }}
            >
              We never share your email. Replies come straight to your inbox.
            </p>
          </div>
        )}
      </aside>
    </>
  );
}

function SuccessState({ ticketId, onClose }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '40px 16px',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: 'var(--ds-green-bg, #ECFDF5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 4,
        }}
      >
        <CheckCircle2 size={28} style={{ color: 'var(--ds-green, #15803D)' }} />
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Message sent</h3>
      <p style={{ fontSize: 13.5, color: 'var(--ds-ink-3)', margin: 0, lineHeight: 1.5 }}>
        We&apos;ll reply to your email within one business day.
      </p>
      {ticketId && (
        <p style={{ fontSize: 12.5, color: 'var(--ds-ink-3)', margin: 0 }}>
          Your reference: <strong style={{ color: 'var(--ds-ink-2)' }}>#{String(ticketId).slice(0, 8)}</strong>
        </p>
      )}
      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 12,
          height: 38,
          padding: '0 20px',
          borderRadius: 9,
          border: '1px solid var(--ds-line-2)',
          background: 'var(--ds-content, #fff)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--ds-ink-2)',
          cursor: 'pointer',
        }}
      >
        Done
      </button>
    </div>
  );
}

// ── Shared style helpers ────────────────────────────────────────────────────

const fieldStyle = {
  marginBottom: 16,
};

const labelStyle = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--ds-ink-2)',
  marginBottom: 7,
};

const ctlStyle = {
  width: '100%',
  height: 40,
  border: '1px solid var(--ds-line-2)',
  borderRadius: 9,
  background: 'var(--ds-content, #fff)',
  padding: '0 12px',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--ds-ink)',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
};
